/**
 * Widgets and the prepaid balance — `modules/usage/routes.ts`.
 *
 * `useWidgets` is read by two screens and by the **nav**: `shell/useNavBadges.ts`
 * fills the Widgets group from it, which is why every write here also invalidates
 * `clients` — a widget renamed or paused changes a sidebar item.
 */

import type { Money, SetAutoTopUpInput } from '@fareast/shared'

import { useApiMutation, useApiQuery } from '../hooks.js'
import { queryKeys } from '../keys.js'
import type { DetailOptions } from './requests.js'

/** `GET /api/clients/me/widgets?period=` — every widget, with this period's totals. */
export function useWidgets(period?: string, options: { readonly inline?: boolean } = {}) {
  return useApiQuery({
    key: queryKeys.usage.widgets(period),
    request: (api) =>
      api.rest.clients.me.widgets.$get({ query: period === undefined ? {} : { period } }),
    /*
     * `inline` is the caller's, and the billing page asks for it: a widgets section
     * that cannot load should be one muted line under its own heading (DESIGN.md
     * P7), because `#widgets` is an anchor other sections link to and a section that
     * vanishes takes the anchor with it. A page whose *subject* is the widget — the
     * widget page itself — wants the boundary, so the default is still to throw.
     */
    ...(options.inline === undefined ? {} : { inline: options.inline }),
  })
}

/**
 * The same call, for the sidebar.
 *
 * `inline: true` and its own cache entry under `clients`: a nav group that cannot
 * load should render nothing, not take the page down — the same argument
 * `useClientOrgLine` makes for the org line.
 */
export function useWidgetNav(options: { enabled?: boolean } = {}) {
  return useApiQuery({
    key: queryKeys.clients.widgets(),
    request: (api) => api.rest.clients.me.widgets.$get({ query: {} }),
    inline: true,
    ...(options.enabled === undefined ? {} : { enabled: options.enabled }),
  })
}

/** `GET /api/clients/me/widgets/:ref?period=` — one widget's whole page. */
export function useWidget(ref: string, period?: string, options: DetailOptions = {}) {
  return useApiQuery({
    key: queryKeys.usage.widget(ref, period),
    request: (api) =>
      api.rest.clients.me.widgets[':ref'].$get({
        param: { ref },
        query: period === undefined ? {} : { period },
      }),
    ...(options.enabled === undefined ? {} : { enabled: options.enabled }),
    ...(options.inline === undefined ? {} : { inline: options.inline }),
  })
}

/** `POST /api/clients/me/widgets/:ref/pause` and `…/resume`. Immediate, undoable. */
export function useSetWidgetPaused() {
  return useApiMutation({
    module: 'usage',
    request: (api, variables: { ref: string; paused: boolean }) =>
      variables.paused
        ? api.rest.clients.me.widgets[':ref'].pause.$post({ param: { ref: variables.ref } })
        : api.rest.clients.me.widgets[':ref'].resume.$post({ param: { ref: variables.ref } }),
    alsoInvalidates: ['clients'],
  })
}

/**
 * `PATCH /api/clients/me/widgets/:ref/cap` — "Change the cap".
 *
 * `money: true`: the cap is the ceiling on what a month can bill, the caller states
 * the worst case it agrees to, and the route carries `idempotent()` so a
 * double-clicked *Save* is one change.
 */
export function useSetWidgetCap() {
  return useApiMutation({
    module: 'usage',
    money: true,
    request: (api, variables: { ref: string; monthlyCap: number; worstCase: Money }, options) =>
      api.rest.clients.me.widgets[':ref'].cap.$patch(
        {
          param: { ref: variables.ref },
          json: { monthly_cap: variables.monthlyCap, worst_case: variables.worstCase },
        },
        { headers: options.headers },
      ),
    alsoInvalidates: ['clients'],
  })
}

/**
 * `PATCH /api/clients/me/widgets/:ref/config` — the settings the client owns.
 *
 * **The body is the keys that changed**, and only the ones the widget's own
 * snapshotted `config_schema` marks `x-editor: client`. `usage.setConfigByClient`
 * merges them over the stored config and validates the whole of it, so a form that
 * shows four properties out of nine cannot silently rewrite the five it does not
 * display (`.plans/ai-product-placer/plan/P5-v1-1.md` item (d1)).
 *
 * **`idempotent: true` and deliberately not `money: true`.** Nothing here commits an
 * amount — a setting is not a charge — so the impersonation refusal `money` carries
 * would be refusing the wrong thing. The header is still sent, because the route
 * carries `idempotent()`: `widget_config_version` is append-only, and a
 * double-clicked *Save* has to be one version rather than two.
 *
 * `alsoInvalidates: ['clients']` for `useSetWidgetPaused`'s reason — `useWidgets` is
 * also the nav's read, under its own `clients` key.
 */
export function useSetWidgetConfig() {
  return useApiMutation({
    module: 'usage',
    idempotent: true,
    request: (api, variables: { ref: string; changes: Record<string, unknown> }, options) =>
      api.rest.clients.me.widgets[':ref'].config.$patch(
        { param: { ref: variables.ref }, json: variables.changes },
        { headers: options.headers },
      ),
    alsoInvalidates: ['clients'],
  })
}

/** `GET /api/clients/me/credits` — the balance, the minimum and the standing top-up. */
export function useCredits() {
  return useApiQuery({
    key: queryKeys.usage.credits(),
    request: (api) => api.rest.clients.me.credits.$get(),
  })
}

/**
 * `PUT /api/clients/me/credits/auto-top-up` — the standing instruction.
 *
 * `money: true`, and only one half of what that flag does is load-bearing here.
 * The route is a `PUT` and carries no `idempotent()` — its own comment says why
 * ("sending it twice with the same body has to mean the same thing") — so the
 * `Idempotency-Key` this adds is read by nothing. The refusal under impersonation
 * is the point: this is a standing authorisation to charge a card off-session, and
 * `usage.autoTopUp` acts on it at 25 past every hour with nobody awake. Staff
 * viewing a client's portal do not give that on their behalf, and
 * `assertMayManageWidget` on the server does **not** refuse them — it reads a role,
 * and an impersonating principal holds the client's.
 *
 * `alsoInvalidates: ['billing']` because `GET /api/clients/me/credits` is `usage`'s
 * and the billing page reads it beside the invoice ledger.
 */
export function useSetAutoTopUp() {
  return useApiMutation({
    module: 'usage',
    money: true,
    request: (api, body: SetAutoTopUpInput, options) =>
      api.rest.clients.me.credits['auto-top-up'].$put({ json: body }, { headers: options.headers }),
    alsoInvalidates: ['billing'],
  })
}
